import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
// components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import GenericList from '~/components/GenericList/GenericList';
import GenericFilter from '~/components/GenericList/GenericFilter';
import TwoFactorHistoryList from './TwoFactorHistoryList';
import UserProfileLogo from '~/components/UserProfileLogo';
import Button from '~/components/CustomButton/CustomButton.jsx';
import Select from '~/elements/Select';
import { getTwoFactorReasonOptions } from '~/utils/constants';
import { Input } from '~/elements/Input.js';

// assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

class TwoFactorUsersList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            history: { userId: null, userName: '' },
            recover: { userId: null, userName: '', comment: '', reason: null },
            isLoading: false,
            page: 0,
            isPageAddedOrEdited: false,
        };
    }

    handlePageChange = (page, isEdit) => {
        this.setState({ page, isPageAddedOrEdited: isEdit });
    };

    handleRecover = async () => {
        const { userStore, commonStore, clientStore, t } = this.props;
        const { recover } = this.state;
        const resolvedComment = recover.reason?.value === t('Other') ? recover.comment.trim() : recover.reason?.value;
        if (!resolvedComment) return;
        this.setState({ isLoading: true });
        try {
            const response = await userStore.recover2FAByAdmin({
                user_id: recover.userId,
                comment: resolvedComment,
                id: userStore.currentUser?.client_id
            });
            commonStore.addNotification(t(response.message), null, 'success');
            this.setState({ recover: { userId: null, userName: '', comment: '', reason: null }, isLoading: false, isPageAddedOrEdited: true });
            userStore.lastListLoadTime = new Date();
        } catch (e) {
            const msg = e?.response?.body?.error || t('Failed to recover 2FA');
            commonStore.addNotification(msg, null, 'error');
            this.setState({ isLoading: false });
        }
    };

    renderTableActionButtons = (id, first_name, last_name, user_type, two_factor_enabled) => {
        const { t } = this.props;
        const name = `${first_name} ${last_name}`;
        return (
            <div className="actions-center">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-history">{t('View')} {t('2FA')} {t('History')}</Tooltip>}
                >
                    <Button
                        icon_sm
                        fill
                        onClick={() => this.setState({ history: { userId: id, userName: name } })}
                    >
                        <i className="fa fa-history" />
                    </Button>
                </OverlayTrigger>
                {two_factor_enabled && user_type !== 'admin' && (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-recover">{t('Recover')} {t('2FA')}</Tooltip>}
                    >
                        <Button
                            icon_sm
                            fill
                            onClick={() => this.setState({ recover: { userId: id, userName: name, comment: '' } })}
                        >
                            <i className="fa fa-unlock-alt" />
                        </Button>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    render() {
        const { t, userStore, commonStore } = this.props;
        const { loading, lastListLoadTime, filters, appliedFilters } = userStore;
        const { history, recover, isLoading, page, isPageAddedOrEdited } = this.state;

        const recoverReasonOptions = getTwoFactorReasonOptions(t);

        return (
            <Fragment>
                <GenericList
                    columns={[
                        {
                            Header: '',
                            id: 'image',
                            accessor: ({ image }) => <UserProfileLogo image_id={image || false} />,
                            maxWidth: 60,
                        },
                        {
                            Header: 'Name',
                            id: 'first_name',
                            accessor: ({ first_name, last_name }) => (
                                <span>
                                    {`${first_name} ${last_name}`}
                                </span>
                            ),
                            isSortable: true,
                            minWidth: 100,
                            maxWidth: 150,
                            textAlign: 'start',
                        },
                        {
                            Header: 'Email',
                            accessor: ({ email }) => <span>{email}</span>,
                            id: 'email',
                            isSortable: true,
                            minWidth: 150,
                            maxWidth: 250,
                            textAlign: 'start',
                        },
                        {
                            Header: 'Role',
                            id: 'user_type',
                            accessor: ({ user_type }) => (
                                <span className="text-capitalize">{t(user_type)}</span>
                            ),
                            isSortable: true,
                            minWidth: 80,
                            maxWidth: 120,
                        },
                        {
                            Header: '2FA',
                            id: 'two_factor_enabled',
                            accessor: ({ two_factor_enabled }) => (
                                <div className={`status-box status-box_default ${two_factor_enabled ? 'status-box_active' : 'status-box_inactive'}`}>
                                    {two_factor_enabled ? t('Enabled') : t('Disabled')}
                                </div>
                            ),
                            minWidth: 80,
                            maxWidth: 120,
                        },
                        {
                            Header: 'Actions',
                            id: 'actions',
                            sortable: false,
                            accessor: ({ id, first_name, last_name, user_type, two_factor_enabled }) =>
                                this.renderTableActionButtons(id, first_name, last_name, user_type, two_factor_enabled),
                            minWidth: 80,
                            maxWidth: 100,
                        },
                    ]}
                    loading={loading}
                    lastListLoadTime={lastListLoadTime}
                    filters={appliedFilters}
                    requestData={(params) => userStore.get2FAUserList(params)}
                    handlePageChange={(p, isEdit) => this.handlePageChange(p, isEdit)}
                    page={page}
                    isPageAddedOrEdited={isPageAddedOrEdited}
                    header={
                        <GenericFilter
                            filters={filters}
                            setFilter={(name, value) => userStore.setFilter(name, value)}
                            onFilter={() => userStore.onFilter()}
                            loading={loading}
                            hasSearch
                            searchFilterName="name"
                            searchFilterPlaceholder="Search"
                        />
                    }
                />

                {/* History Modal */}
                <Modal
                    className='right modal_revised'
                    size="lg"
                    show={!!history.userId}
                    onHide={() => this.setState({ history: { userId: null, userName: '' } })}
                >
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0">
                            {t('2FA')} {t('History')} — {history.userName}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt="close"
                            onClick={() => this.setState({ history: { userId: null, userName: '' } })}
                        />
                    </Modal.Header>
                    <Modal.Body >
                        {history.userId && <TwoFactorHistoryList userId={history.userId} />}
                    </Modal.Body>
                </Modal>

                {/* Recover 2FA Modal */}
                <Modal
                    show={!!recover.userId}
                    onHide={() => this.setState({ recover: { userId: null, userName: '', comment: '', reason: null } })}
                >
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0">
                            {t('Recover')} {t('2FA')} — {recover.userName}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt="close"
                            onClick={() => this.setState({ recover: { userId: null, userName: '', comment: '', reason: null } })}
                        />
                    </Modal.Header>
                    <Modal.Body style={{ minHeight: '200px' }}>
                        <div className="form-group">
                            <label>{t('Reason')} <span className="text-danger">*</span></label>
                            <Select
                                options={recoverReasonOptions}
                                value={recover.reason}
                                onChange={(selected) => this.setState({ recover: { ...recover, reason: selected, comment: '' } })}
                                placeholder={t('Select a reason')}
                                required
                            />
                        </div>
                        {recover.reason?.value === t('Other') && (
                            <Input
                                className="mt-2"
                                placeholder={t('Enter a reason')}
                                value={recover.comment}
                                onChange={(e) => this.setState({ recover: { ...recover, comment: e.target.value } })}
                            />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            fill
                            onClick={this.handleRecover}
                            disabled={isLoading || !recover.reason || (recover.reason?.value === t('Other') && !recover.comment.trim())}
                        >
                            {isLoading ? `${t('Recovering')}...` : `${t('Recover')} ${t('2FA')}`}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Fragment>
        );
    }
}

export default inject('userStore', 'commonStore', 'clientStore')(applicationRouter(withLocalization(observer(TwoFactorUsersList))));
